import React, { useState, useEffect, useContext } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import styles from './ProductList.module.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

function resolveImageUrl(url) {
  if (!url || typeof url !== 'string' || url.trim() === '') return '/placeholder-image.png';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  // Relative path like /uploads/images/... — prefix with the API base URL
  const path = url.startsWith('/') ? url : `/${url}`;
  return `${API_BASE}${path}`;
}

function formatDateTime(dateString) {
  const d = new Date(dateString);
  let hours = d.getHours();
  const minutes = d.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  const time = `${hours}:${minutes}${ampm}`;
  const date = `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth()+1).toString().padStart(2, '0')}/${d.getFullYear()}`;
  return `${time} - ${date}`;
}

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchBy, setSearchBy] = useState('both'); // 'both', 'name', or 'itemCode'
  const [copyLoading, setCopyLoading] = useState(false);
  const [copyMessage, setCopyMessage] = useState('');
  const [copyError, setCopyError] = useState('');
  const [copyProgress, setCopyProgress] = useState(null); // { page, totalPages, total, copied, imagesUploaded }
  const [editingProduct, setEditingProduct] = useState(null);
  const [editFormData, setEditFormData] = useState({ name: '', itemCode: '', quantity: 0, image: null, imagePreview: '' });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [quantityProduct, setQuantityProduct] = useState(null);
  const [newQuantity, setNewQuantity] = useState('');
  const [quantityLoading, setQuantityLoading] = useState(false);
  const { isAdmin } = useContext(AuthContext);
  const { branch } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
  }, [branch]);

  // Server-side search against full D1 database
  useEffect(() => {
    const query = searchQuery.trim();
    if (!query) {
      setFilteredProducts(products);
      return;
    }

    let cancelled = false;
    const doSearch = async () => {
      try {
        setSearchLoading(true);
        const token = localStorage.getItem('token');
        const response = await axios.get(`/api/products/${branch}/search`, {
          params: { query, searchBy, limit: 500 },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && response.data.success) {
          setFilteredProducts(response.data.products);
        }
      } catch {
        // fallback: filter locally from loaded products
        if (!cancelled) {
          const keywords = query.toLowerCase().split(/\s+/).filter(Boolean);
          setFilteredProducts(
            products.filter(p => {
              return keywords.every(kw => {
                const inName = searchBy !== 'itemCode' && p.name.toLowerCase().includes(kw);
                const inCode = searchBy !== 'name' && p.item_code.toLowerCase().includes(kw);
                return inName || inCode;
              });
            })
          );
        }
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    };

    // Debounce 300ms
    const timer = setTimeout(doSearch, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [searchQuery, searchBy, branch]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      // Load up to 5000 products so all are available for browsing
      const response = await axios.get(`/api/products/${branch}`, {
        params: { limit: 5000 },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setProducts(response.data.products);
        setFilteredProducts(response.data.products);
      } else {
        setError('Failed to fetch products');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setError('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyNow = async () => {
    if (!window.confirm(`Copy all products and images for branch "${branch}" to Cloudflare?\nThis may take a few minutes for large branches.`)) {
      return;
    }

    setCopyLoading(true);
    setCopyError('');
    setCopyMessage('');
    setCopyProgress(null);

    const token = localStorage.getItem('token');
    const CHUNK_SIZE = 200; // products per request

    let page = 1;
    let totalCopied = 0;
    let totalImages = 0;
    let totalMissing = 0;
    let totalPages = null;
    let totalProducts = null;

    try {
      while (true) {
        const response = await axios.post(
          '/api/migration/copy-now',
          { branch, page, limit: CHUNK_SIZE },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const data = response.data;
        if (!data?.success) {
          setCopyError(data?.message || 'Copy failed');
          break;
        }

        totalCopied += data.copied || 0;
        totalImages += data.imagesUploaded || 0;
        totalMissing += data.imagesMissing || 0;
        totalPages = data.totalPages || totalPages;
        totalProducts = data.total || totalProducts;

        setCopyProgress({
          page: data.page,
          totalPages: data.totalPages,
          total: data.total,
          copied: totalCopied,
          imagesUploaded: totalImages,
        });

        if (!data.hasNextPage) break;
        page += 1;
      }

      setCopyMessage(
        `✅ Copy complete! ${totalCopied} products copied, ${totalImages} images uploaded, ${totalMissing} images missing.`
      );
    } catch (err) {
      setCopyError(err.response?.data?.message || err.message || 'Copy failed');
    } finally {
      setCopyLoading(false);
    }
  };

  const openQuantityModal = (product) => {
    setQuantityProduct(product);
    setNewQuantity(product.quantity.toString());
  };

  const closeQuantityModal = () => {
    setQuantityProduct(null);
    setNewQuantity('');
  };

  const handleQuantitySubmit = async (e) => {
    e.preventDefault();
    const quantity = parseInt(newQuantity);
    if (isNaN(quantity) || quantity < 0) {
      alert('Please enter a valid quantity (0 or positive number)');
      return;
    }
    
    try {
      setQuantityLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.patch(`/api/products/${branch}/${quantityProduct.id}/quantity`, 
        { quantity },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        // Update product in state
        const updateProd = product => product.id === quantityProduct.id ? { ...product, quantity } : product;
        setProducts(products.map(updateProd));
        setFilteredProducts(filteredProducts.map(updateProd));
        closeQuantityModal();
      } else {
        alert('Failed to update quantity');
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
      alert('Failed to update quantity');
    } finally {
      setQuantityLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/products/${branch}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts(products.filter((p) => p.id !== id));
    } catch (error) {
      alert('Failed to delete product');
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setFilteredProducts(products);
  };

  // --- Edit Modal Handlers ---
  const openEditModal = (product) => {
    setEditingProduct(product);
    setEditFormData({
      name: product.name,
      itemCode: product.item_code,
      quantity: product.quantity,
      image: null,
      imagePreview: resolveImageUrl(product.image_url)
    });
    setEditError('');
  };

  const closeEditModal = () => {
    setEditingProduct(null);
  };

  const handleEditImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditFormData(prev => ({
        ...prev,
        image: file,
        imagePreview: window.URL.createObjectURL(file)
      }));
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editFormData.name || !editFormData.itemCode) {
      setEditError('Name and item code are required');
      return;
    }
    try {
      setEditLoading(true);
      setEditError('');
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('name', editFormData.name);
      formData.append('itemCode', editFormData.itemCode);
      formData.append('quantity', editFormData.quantity);
      if (editFormData.image) {
        formData.append('image', editFormData.image);
      }
      
      const response = await axios.put(`/api/products/${branch}/${editingProduct.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const newImageUrl = response.data.product?.image_url || editFormData.imagePreview;
        const updateProd = p => p.id === editingProduct.id ? { 
          ...p, 
          name: editFormData.name, 
          item_code: editFormData.itemCode, 
          quantity: editFormData.quantity, 
          image_url: newImageUrl 
        } : p;
        
        setProducts(list => list.map(updateProd));
        setFilteredProducts(list => list.map(updateProd));
        closeEditModal();
      } else {
        setEditError(response.data.message || 'Failed to update product');
      }
    } catch (error) {
      setEditError(error.response?.data?.message || 'Failed to update product');
    } finally {
      setEditLoading(false);
    }
  };

  // --- Export products as CSV ---
  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/products/${branch}/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      // Create a download link for the CSV file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `products_${branch}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Failed to export products');
      console.error('Export error:', error);
    }
  };

  if (loading && products.length === 0) {
    return <div className="text-center mt-5">Loading products...</div>;
  }

  return (
    <div>
      <div className="d-flex justify-content-center justify-content-md-between align-items-center mb-4 flex-wrap gap-2">
        {isAdmin() && (
          <div className="dropdown w-100 text-center text-md-end w-md-auto">
            <button className="btn btn-secondary dropdown-toggle w-100 w-md-auto" type="button" data-bs-toggle="dropdown" aria-expanded="false">
              ⚙️ Admin Options
            </button>
            <ul className="dropdown-menu dropdown-menu-md-end w-100 w-md-auto text-center text-md-start shadow">
              <li><Link to={`/${branch}/add-product`} className="dropdown-item text-success fw-bold py-2">Add Product</Link></li>
              <li><Link to={`/${branch}/import-products`} className="dropdown-item text-primary py-2">Import Products</Link></li>
              <li><hr className="dropdown-divider" /></li>
              <li><button onClick={handleExport} className="dropdown-item py-2">Export Products</button></li>
              {process.env.REACT_APP_IS_CLOUDFLARE === 'true' && (
                <>
                  <li><hr className="dropdown-divider" /></li>
                  <li>
                    <button onClick={handleCopyNow} className="dropdown-item fw-semibold py-2" disabled={copyLoading}>
                      {copyLoading ? 'Copying...' : 'Copy Now (to Cloudflare)'}
                    </button>
                  </li>
                </>
              )}
            </ul>
          </div>
        )}
      </div>

      {copyError && (
        <div className="alert alert-danger" role="alert">
          {copyError}
        </div>
      )}

      {copyMessage && (
        <div className="alert alert-success" role="alert">
          {copyMessage}
        </div>
      )}

      {copyLoading && copyProgress && (
        <div className="card mb-3 border-0 shadow-sm">
          <div className="card-body py-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <small className="text-muted fw-semibold">
                Copying <strong>{branch}</strong> — Page {copyProgress.page} of {copyProgress.totalPages}
              </small>
              <small className="text-muted">
                {copyProgress.copied} / {copyProgress.total} products
              </small>
            </div>
            <div className="progress" style={{ height: '10px' }}>
              <div
                className="progress-bar progress-bar-striped progress-bar-animated bg-dark"
                role="progressbar"
                style={{ width: `${Math.round((copyProgress.page / copyProgress.totalPages) * 100)}%` }}
              />
            </div>
            <small className="text-muted mt-1 d-block">
              📦 {copyProgress.copied} products copied &nbsp;·&nbsp; 🖼️ {copyProgress.imagesUploaded} images uploaded
            </small>
          </div>
        </div>
      )}
      
      <div className="card mb-3 shadow-sm">
        <div className="card-body p-2">
          <form onSubmit={e => e.preventDefault()}>
            <div className="row g-2 align-items-center">
              <div className="col position-relative">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Search products"
                />
                {searchLoading && (
                  <div className="position-absolute end-0 top-50 translate-middle-y me-3">
                    <div className="spinner-border spinner-border-sm text-secondary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                )}
              </div>
              <div className="col-auto">
                <button type="button" className="btn btn-secondary" onClick={clearSearch}>Clear</button>
              </div>
            </div>
          </form>
        </div>
      </div>
      
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      
      {filteredProducts.length === 0 ? (
        <div className="alert alert-info">
          No products found. {isAdmin() && 'Add some products to get started.'}
        </div>
      ) : (
        <div>
          <p className="text-muted mb-3">Showing {filteredProducts.length} products</p>
          <div className="row row-cols-2 row-cols-md-3 row-cols-lg-4 g-2">
            {filteredProducts.map(product => (
              <div key={product.id} className="col">
                <div className="card h-100 product-card shadow-sm">
                  <div className="card-img-top-container">
                    <img 
                      src={resolveImageUrl(product.image_url)} 
                      className="card-img-top" 
                      alt={product.name}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/placeholder-image.png';
                      }}
                    />
                  </div>
                  <div className="card-body" style={{ padding: '6px' }}>
                    <h6 className="card-title mb-1 fw-bold" style={{ fontSize: '0.8rem', lineHeight: '1.2' }}>{product.name}</h6>
                    <p className="card-text small mb-0" style={{ fontSize: '0.75rem' }}>
                      <strong>Code:</strong> {product.item_code}<br />
                      <strong>Qty:</strong> {product.quantity}
                    </p>
                  </div>
                  <div className="card-footer bg-transparent border-0 d-flex flex-column gap-1 pt-0" style={{ padding: '6px' }}>
                    {isAdmin() && product.last_stock_update && (
                      <div className="text-muted text-center" style={{ fontSize: '0.65rem' }}>
                        Updated: {formatDateTime(product.last_stock_update)}
                      </div>
                    )}
                    {isAdmin() && (
                      <div className="d-flex gap-1 mt-1">
                        <button onClick={() => openEditModal(product)} className="btn btn-sm btn-warning flex-fill py-1" style={{ fontSize: '0.8rem' }}>Edit</button>
                        <button onClick={() => handleDelete(product.id)} className="btn btn-sm btn-danger flex-fill py-1" style={{ fontSize: '0.8rem' }}>Delete</button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Quantity Update Modal */}
      {quantityProduct && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1" role="dialog" aria-modal="true">
          <div className="modal-dialog modal-dialog-centered modal-sm" role="document">
            <div className="modal-content">
              <div className="modal-header py-2">
                <h6 className="modal-title mb-0">Update Quantity</h6>
                <button type="button" className="btn-close" onClick={closeQuantityModal} aria-label="Close"></button>
              </div>
              <div className="modal-body py-3">
                <form onSubmit={handleQuantitySubmit}>
                  <div className="mb-3 text-center">
                    <strong className="d-block mb-1">{quantityProduct.name}</strong>
                    <span className="text-muted small">Code: {quantityProduct.item_code}</span>
                  </div>
                  <div className="mb-3">
                    <input 
                      type="number" 
                      className="form-control form-control-lg text-center" 
                      value={newQuantity} 
                      onChange={e => setNewQuantity(e.target.value)} 
                      min="0" 
                      autoFocus
                    />
                  </div>
                  <div className="d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={closeQuantityModal}>Cancel</button>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={quantityLoading}>
                      {quantityLoading ? 'Saving...' : 'Update'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Product Modal */}
      {editingProduct && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1" role="dialog" aria-modal="true">
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Product</h5>
                <button type="button" className="btn-close" onClick={closeEditModal} aria-label="Close"></button>
              </div>
              <div className="modal-body">
                {editError && <div className="alert alert-danger p-2">{editError}</div>}
                <form onSubmit={handleEditSubmit}>
                  <div className="mb-2">
                    <label className="form-label small mb-1">Name</label>
                    <input type="text" className="form-control form-control-sm" value={editFormData.name} onChange={e => setEditFormData({...editFormData, name: e.target.value})} required />
                  </div>
                  <div className="mb-2">
                    <label className="form-label small mb-1">Item Code</label>
                    <input type="text" className="form-control form-control-sm" value={editFormData.itemCode} onChange={e => setEditFormData({...editFormData, itemCode: e.target.value})} required />
                  </div>
                  <div className="mb-2">
                    <label className="form-label small mb-1">Quantity</label>
                    <input type="number" className="form-control form-control-sm" value={editFormData.quantity} onChange={e => setEditFormData({...editFormData, quantity: Number(e.target.value)})} min="0" />
                  </div>
                  <div className="mb-3">
                    <label className="form-label small mb-1">Product Image</label>
                    <input type="file" className="form-control form-control-sm" accept="image/*" onChange={handleEditImageChange} />
                    {editFormData.imagePreview && (
                      <div className="mt-2 text-center">
                        <img src={editFormData.imagePreview} alt="Preview" className="img-thumbnail" style={{ maxHeight: '150px' }} 
                          onError={(e) => { e.target.onerror = null; e.target.src = '/placeholder-image.png'; }} />
                      </div>
                    )}
                  </div>
                  <div className="d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={closeEditModal}>Cancel</button>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={editLoading}>
                      {editLoading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ProductList;
